import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';

import { trackAction } from '@ctct/distui-helpers';
import { Alert, Button, Dialog } from '@ctct/ui-react';

import BillingSvcLite from '../../api/BillingSvcApi';
import { getBillingCustomerCurrency, getAccountId, getUserName } from '../../api/DistUI';
import MyAccountApiLite from '../../api/MyAccountApiLite';
import SMSTiersTable from '../SMSTiersTable';

const { getUserPlanOverview, getSMSTiers, getPlanGroup } = MyAccountApiLite;
const { setSMSMinimumUsage, getRecurringUsage } = BillingSvcLite;

const SMSUpdateTierDialog = ({ t, open, closeDialog, closeOnSuccess, smsUpdatedCallback }) => {
  const [initiallySelectedSMSTier, setInitiallySelectedSMSTier] = useState();
  const [smsSelection, setSmsSelection] = useState();
  const [contactCount, setContactCount] = useState();
  const [updateSmsTier, setUpdateSmsTier] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [successfulUpdate, setSuccessfulUpdate] = useState(false);
  const [accountId, setAccountId] = useState();
  const [userName, setUserName] = useState();
  const [currency, setCurrency] = useState('USD');
  const [SMSTiers, setSMSTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackAction({ action: 'update-sms-dialog-view' });
  }, []);

  useEffect(() => {
    if (updateSmsTier) {
      (async () => {
        setErrorMessage(null);

        try {
          await setSMSMinimumUsage({ accountId, newFloor: smsSelection.min, updateBy: userName });

          if (smsUpdatedCallback) smsUpdatedCallback(smsSelection);

          setSuccessfulUpdate(true);

          if (closeOnSuccess) {
            closeDialog();
          }
        } catch (err) {
          const { response } = err;

          const { data: errMessage } = response || {};

          if (errMessage && typeof errMessage === 'string') {
            setErrorMessage(errMessage);
          } else {
            setErrorMessage(t('sms-plans.unknown_error'));
          }
        } finally {
          setUpdateSmsTier(false);
        }
      })();
    }
  }, [updateSmsTier]);

  useEffect(() => {
    if (initiallySelectedSMSTier) setLoading(false);
  }, [initiallySelectedSMSTier]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const localCurrency = await getBillingCustomerCurrency();
      const { currentPlanId } = await getPlanGroup(localCurrency);

      setAccountId(await getAccountId());
      setUserName(await getUserName());

      const [{ current_contacts: contactCountLocal }, localSMSTiers, { data }] = await Promise.all([
        getUserPlanOverview(),
        getSMSTiers(localCurrency, currentPlanId),
        getRecurringUsage(),
      ]);

      setSMSTiers(localSMSTiers);
      setContactCount(contactCountLocal ? parseInt(contactCountLocal.replace(/,/g, ''), 10) : 0);
      setCurrency(localCurrency);

      let initialTier = null;

      if (data && data.minValue != null) {
        initialTier = { min: data.minValue };
      }

      setInitiallySelectedSMSTier(initialTier);
      setSmsSelection(initialTier);
    })();
  }, []);

  let initiallySelectedTierMin = 0;

  if (initiallySelectedSMSTier) {
    initiallySelectedTierMin = successfulUpdate ? smsSelection.min : initiallySelectedSMSTier.min;
  }

  // TODO add actual learn more URL @61
  return (
    <Dialog
      heading={t('update-sms-plans.name')}
      data-qe-id="update-sms-plans-dialog"
      contentFlush={false}
      kind="takeover"
      isOpen={open}
      onDismiss={closeDialog}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexBasis: 'min-content',
          flexDirection: 'column',
          marginRight: 'auto',
          marginLeft: 'auto',
          maxWidth: 825,
        }}
        className="update-sms-tiers-table"
      >
        <header style={{ textAlign: 'center' }}>
          <div className="content-left">{t('update-sms-plans.dialog.subtitle-note')}</div>
        </header>

        {errorMessage && <Alert kind="error" message={errorMessage} />}

        {successfulUpdate && (
          <Alert
            kind="success"
            heading={t('sms-plans.sms_update_banner_header')}
            message={t('sms-plans.sms_update_banner_message')}
          />
        )}

        <div
          className="learn-more-about-sms"
          style={{
            display: 'flex',
            flexDirection: 'row-reverse',
            paddingBottom: 4,
            marginTop: 30,
          }}
        >
          <a
            href="https://knowledgebase.constantcontact.com/articles/KnowledgeBase/46517-Add-SMS-to-your-Constant-Contact-email-plan"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackAction({ action: 'update-sms-learn-more-select' });
            }}
          >
            {t('sms-plans.learn-more')}
          </a>
        </div>
        <div style={{ marginBottom: 20 }}>
          <SMSTiersTable
            currency={currency}
            selectedSMSTier={smsSelection}
            initiallySelectedTierMin={initiallySelectedTierMin}
            setSelectedSMSTier={(min, max, price) => {
              trackAction({ action: 'update-sms-tier-select' });
              setSmsSelection({ min, max, price });
            }}
            SMSTiers={SMSTiers}
            loading={loading}
            contactCountLowEnd={contactCount}
            allDisabled={successfulUpdate}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
          {successfulUpdate ? (
            <div>
              <Button
                block
                className="sidebar-close-button"
                kind="primary"
                data-qe-id="sms-plans-dialog-close-button"
                onClick={() => {
                  // no tracking necessary, just closing
                  closeDialog();
                }}
                text={t('sms-plans.close')}
              />
            </div>
          ) : (
            <>
              <div>
                <Button
                  block
                  disabled={
                    initiallySelectedSMSTier && smsSelection && initiallySelectedSMSTier.min === smsSelection.min
                  }
                  className="sidebar-continue-button"
                  kind="primary"
                  data-qe-id="update-sms-plans-dialog-update-button"
                  onClick={() => {
                    trackAction({ action: 'update-sms-button-update' });
                    setUpdateSmsTier(true);
                  }}
                  text={t('update-sms-plans.dialog.change-plan')}
                />
              </div>
              <div style={{ marginRight: 20 }}>
                <Button
                  block
                  className="sidebar-continue-button"
                  kind="secondary"
                  data-qe-id="update-sms-plans-dialog-cancel-button"
                  onClick={() => {
                    trackAction({ action: 'update-sms-cancel' });
                    closeDialog();
                  }}
                  text={t('update-sms-plans.dialog.cancel-button')}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

SMSUpdateTierDialog.defaultProps = {
  closeOnSuccess: false,
  smsUpdatedCallback: null,
};

SMSUpdateTierDialog.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
  closeOnSuccess: PropTypes.bool,
  smsUpdatedCallback: PropTypes.func,
};

export default withTranslation()(SMSUpdateTierDialog);
